<template>
  <div class="user-info">
    <!-- Client selector when multiple clients available -->
    <el-select v-if="clients && clients.length > 1" v-model="selectedClientId" @change="handleClientChange"
      placeholder="Select client" size="small" class="client-selector">
      <el-option v-for="client in clients" :key="client.id" :label="client.name" :value="client.id" />
    </el-select>

    <!-- Display selected client name when only one client or client is selected -->
    <span v-else-if="selectedClient" class="user-info-name">{{ selectedClient.name }}</span>

    <el-dropdown trigger="click" @command="handleUserMenuCommand">
      <span class="user-menu-trigger" role="button" tabindex="0">
        <el-avatar :size="36">{{ userInitials }}</el-avatar>
      </span>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item v-if="userDisplayName" disabled class="user-dropdown-display-name">
            {{ userDisplayName }}
          </el-dropdown-item>
          <el-dropdown-item command="change-password" :divided="!!userDisplayName">Změna hesla</el-dropdown-item>
          <el-dropdown-item command="logout" divided>Odhlásit</el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, toValue } from 'vue'
import { useRouter } from 'vue-router'
import {
  ElAvatar,
  ElDropdown,
  ElDropdownMenu,
  ElDropdownItem,
  ElSelect,
  ElOption,
} from 'element-plus'
import { storeToRefs } from 'pinia'
import { useAuth } from '@/composables/useAuth'
import { useCustomerStore } from '@/stores/customer'

const { user, logout } = useAuth()
const router = useRouter()
const customerStore = useCustomerStore()

const { clients, selectedClient, selectedClientId, routeContext } = storeToRefs(customerStore)

/** Client id for router params — same source as store APIs: routeContext, then URL. */
const clientIdForRoutes = computed(() =>
  String(routeContext.value.clientId || 0),
)

const userInitials = computed(() => {
  const u = toValue(user)
  if (!u) return '?'
  const fn = (u.firstName || '').trim()
  const ln = (u.lastName || '').trim()
  if (fn && ln) return (fn[0] + ln[0]).toUpperCase()
  const parts = fn.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  if (fn.length >= 2) return fn.slice(0, 2).toUpperCase()
  const email = (u.email || '').trim()
  if (email.length >= 2) return email.slice(0, 2).toUpperCase()
  return '?'
})

const userDisplayName = computed(() => {
  const u = toValue(user)
  if (!u) return ''
  const fn = (u.firstName || '').trim()
  const ln = (u.lastName || '').trim()
  if (fn && ln) return `${fn} ${ln}`
  if (fn) return fn
  if (ln) return ln
  const email = (u.email || '').trim()
  if (email) return email
  return (u.username || '').trim()
})

function handleClientChange() {
  // The selectedClientId computed property in the store will handle the client switching
  // We just need to reload dashboard info for the new client
}

async function handleLogout() {
  await logout()
}

function handleUserMenuCommand(command: string) {
  if (command === 'logout') {
    void handleLogout()
    return
  }
  if (command === 'change-password') {
    void router.push({
      name: 'change-password',
      params: { clientId: clientIdForRoutes.value },
    })
  }
}
</script>

<style scoped>
.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.user-info-name {
  font-size: 14px;
  color: var(--el-text-color-regular);
  white-space: nowrap;
}

.client-selector {
  min-width: 150px;
  max-width: 200px;
}

.user-menu-trigger {
  display: inline-flex;
  cursor: pointer;
  outline: none;
}

.user-menu-trigger:focus-visible {
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--el-color-primary-light-5);
}

:deep(.user-dropdown-display-name.is-disabled) {
  cursor: default;
  opacity: 1;
  color: var(--el-text-color-primary);
  font-weight: 500;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .user-info {
    gap: 8px;
  }

  .user-info-name {
    display: none;
  }

  .client-selector {
    min-width: 120px;
    max-width: 150px;
  }
}

@media (max-width: 480px) {
  .user-info {
    gap: 4px;
  }
        .client-selector {
          min-width: 100px;
          max-width: 120px;
        }
}
</style>
